import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ImportService, ImportAnalysis, ImportType } from '../../../services/import.service';
import { FileUploadModule } from 'primeng/fileupload';
import { StepperModule } from 'primeng/stepper';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TableModule } from 'primeng/table';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { RouterModule } from '@angular/router';

@Component({
    selector: 'app-import',
    standalone: true,
    imports: [
        CommonModule,
        FileUploadModule,
        StepperModule,
        SelectButtonModule,
        TableModule,
        CardModule,
        ButtonModule,
        TagModule,
        FormsModule,
        ToastModule,
        ProgressSpinnerModule,
        RouterModule
    ],
    providers: [MessageService],
    template: `
    <div class="p-4 bg-gray-50 min-h-screen">
      <div class="max-w-4xl mx-auto">
        <div class="mb-6 flex justify-between items-center">
            <div>
                <h1 class="text-4xl font-extrabold text-slate-800 m-0">AI Data Import</h1>
                <p class="text-slate-500 mt-2">Revolutionize your workflow with AI-assisted data ingestion.</p>
            </div>
            <p-tag severity="info" value="AI Powered" icon="pi pi-sparkles" [rounded]="true"></p-tag>
        </div>

        <p-toast></p-toast>

        <p-card styleClass="shadow-lg border-round-xl overflow-hidden">
          <p-stepper [value]="activeStep">
            <p-step-list>
                <p-step [value]="0">Upload</p-step>
                <p-step [value]="1">AI Analysis</p-step>
                <p-step [value]="2">Finish</p-step>
            </p-step-list>
            
            <p-step-panels>
                <!-- STEP 0: SELECT TYPE & UPLOAD -->
                <p-step-panel [value]="0">
                  <div class="p-4">
                    <h2 class="text-xl font-bold mb-4">What would you like to import?</h2>
                    <div class="flex flex-column gap-4 align-items-center py-4">
                      <p-selectbutton 
                          [options]="importOptions" 
                          [(ngModel)]="selectedType" 
                          optionLabel="label" 
                          optionValue="value"
                      ></p-selectbutton>

                      <div class="w-full border-2 border-dashed border-300 border-round-xl p-6 text-center cursor-pointer hover:border-primary transition-colors bg-white shadow-sm"
                           (click)="fileInput.click()">
                          <i class="pi pi-cloud-upload text-6xl text-primary mb-3"></i>
                          <p class="text-xl font-medium text-slate-700">Drop your file here or click to browse</p>
                          <p class="text-slate-400">CSV, Excel, or JSON files supported</p>
                          <input #fileInput type="file" class="hidden" (change)="onFileSelect($event)">
                      </div>

                      <div class="flex justify-content-center mt-4 text-slate-400">
                          <div class="flex align-items-center gap-2">
                              <i class="pi pi-shield"></i>
                              <span class="text-sm">Secure, encrypted processing</span>
                          </div>
                      </div>
                    </div>
                  </div>
                </p-step-panel>

                <!-- STEP 1: AI ANALYSIS -->
                <p-step-panel [value]="1">
                  <div class="p-4">
                    <div *ngIf="analyzing" class="text-center py-8">
                      <p-progressspinner styleClass="w-4rem h-4rem" strokeWidth="4" fill="var(--surface-ground)" animationDuration=".5s"></p-progressspinner>
                      <h2 class="text-2xl font-bold text-primary mt-4 animate-pulse">AI is analyzing your data...</h2>
                      <p class="text-slate-500">Mapping fields, identifying entities, and checking for common patterns.</p>
                    </div>

                    <div *ngIf="!analyzing && analysis">
                      <div class="flex align-items-start gap-4 p-4 bg-primary-50 border-round-lg mb-4 border-left-4 border-primary">
                          <i class="pi pi-info-circle text-primary text-2xl"></i>
                          <div>
                              <h3 class="text-primary font-bold m-0">Analysis Complete</h3>
                              <p class="text-primary-700 mt-1 mb-0">{{ analysis.summary }}</p>
                          </div>
                      </div>

                      <h3 class="text-xl font-bold mb-3 flex align-items-center gap-2">
                          <i class="pi pi-eye text-slate-500"></i>
                          Data Preview
                      </h3>
                      
                      <p-table [value]="analysis.previewItems" [rows]="5" styleClass="p-datatable-sm shadow-1 border-round-lg overflow-hidden">
                        <ng-template pTemplate="header">
                          <tr *ngIf="selectedType === 'accounts'">
                            <th>Account Name</th>
                            <th>Type</th>
                            <th class="text-right">Balance</th>
                          </tr>
                          <tr *ngIf="selectedType === 'transactions'">
                            <th>Date</th>
                            <th>Description</th>
                            <th class="text-right">Amount</th>
                          </tr>
                          <tr *ngIf="selectedType === 'contacts'">
                            <th>Name</th>
                            <th>Role</th>
                            <th>Email</th>
                          </tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-item>
                          <tr *ngIf="selectedType === 'accounts'">
                            <td class="font-medium">{{ item.name }}</td>
                            <td><p-tag [value]="item.type" severity="info" [rounded]="true"></p-tag></td>
                            <td class="text-right font-bold">{{ item.current_balance | currency }}</td>
                          </tr>
                          <tr *ngIf="selectedType === 'transactions'">
                            <td>{{ item.date }}</td>
                            <td class="font-medium">{{ item.description }}</td>
                            <td class="text-right font-bold text-slate-700">{{ item.amount | currency }}</td>
                          </tr>
                          <tr *ngIf="selectedType === 'contacts'">
                            <td class="font-medium">{{ item.name }}</td>
                            <td><p-tag [value]="item.type" [severity]="item.type === 'customer' ? 'success' : 'warn'" [rounded]="true"></p-tag></td>
                            <td>{{ item.email }}</td>
                          </tr>
                        </ng-template>
                      </p-table>

                      <div class="flex justify-content-between mt-6 pt-4 border-top-1 border-200">
                        <p-button label="Discard" icon="pi pi-trash" [text]="true" severity="danger" (onClick)="reset()"></p-button>
                        <p-button label="Confirm & Import" icon="pi pi-cloud-download" severity="success" (onClick)="executeImport()"></p-button>
                      </div>
                    </div>
                  </div>
                </p-step-panel>

                <!-- STEP 2: SUCCESS -->
                <p-step-panel [value]="2">
                  <div class="p-8 text-center bg-green-50 border-round-xl">
                    <div class="bg-green-500 text-white w-5rem h-5rem border-circle flex items-center justify-center mx-auto mb-4 shadow-lg animate-bounce">
                      <i class="pi pi-check text-4xl"></i>
                    </div>
                    <h2 class="text-3xl font-bold text-slate-800">Success!</h2>
                    <p class="text-xl text-slate-600 mb-6">Your data has been successfully imported and mapped.</p>
                    
                    <div class="flex justify-content-center gap-3">
                      <p-button label="Return to Dashboard" icon="pi pi-home" [text]="true" routerLink="/dashboard"></p-button>
                      <p-button label="Start Another Import" icon="pi pi-plus" (onClick)="reset()"></p-button>
                    </div>
                  </div>
                </p-step-panel>
            </p-step-panels>

          </p-stepper>
        </p-card>

        <div class="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div class="p-4 bg-white border-round-xl shadow-sm border-left-4 border-blue-500">
                <i class="pi pi-bolt text-blue-500 text-xl mb-2"></i>
                <h4 class="m-0 font-bold">Fast Mapping</h4>
                <p class="text-sm text-slate-500 mb-0">AI automatically recognizes column headers and data types.</p>
            </div>
            <div class="p-4 bg-white border-round-xl shadow-sm border-left-4 border-purple-500">
                <i class="pi pi-user-plus text-purple-500 text-xl mb-2"></i>
                <h4 class="m-0 font-bold">Smart Contacts</h4>
                <p class="text-sm text-slate-500 mb-0">Identifies vendors and customers based on transaction context.</p>
            </div>
            <div class="p-4 bg-white border-round-xl shadow-sm border-left-4 border-orange-500">
                <i class="pi pi-exclamation-triangle text-orange-500 text-xl mb-2"></i>
                <h4 class="m-0 font-bold">Conflict Check</h4>
                <p class="text-sm text-slate-500 mb-0">AI flags potential duplicate entries before they hit your ledger.</p>
            </div>
        </div>
      </div>
    </div>
  `,
    styles: [`
    :host ::ng-deep {
        .p-stepper {
            flex-basis: 50rem;
        }
        .p-stepper-tab {
            background-color: transparent !important;
        }
        .p-button.p-button-text:hover {
            background-color: transparent;
        }
    }
    .animate-pulse {
        animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    }
    @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: .5; }
    }
  `]
})
export class ImportComponent implements OnInit {
    activeStep = 0;
    selectedType: ImportType = 'transactions';
    importOptions = [
        { label: 'Accounts', value: 'accounts' },
        { label: 'Transactions', value: 'transactions' },
        { label: 'Contacts', value: 'contacts' }
    ];

    analyzing = false;
    analysis: ImportAnalysis | null = null;

    constructor(
        private importService: ImportService,
        private messageService: MessageService
    ) { }

    ngOnInit() { }

    onFileSelect(event: any) {
        const file = event.target.files[0];
        if (!file) return;

        this.activeStep = 1;
        this.analyzing = true;

        this.importService.analyzeFile(file, this.selectedType).subscribe({
            next: (res) => {
                setTimeout(() => {
                    this.analysis = res;
                    this.analyzing = false;
                }, 1500); // Artificial delay to make AI feel "real"
            },
            error: () => {
                this.messageService.add({ severity: 'error', summary: 'Analysis Failed', detail: 'The AI could not process this file.' });
                this.analyzing = false;
            }
        });
    }

    executeImport() {
        if (!this.analysis) return;

        this.importService.executeImport(this.analysis.id).subscribe({
            next: (res) => {
                this.messageService.add({ severity: 'success', summary: 'Import Successful', detail: `Imported ${res.count} items.` });
                this.activeStep = 2;
            },
            error: () => {
                this.messageService.add({ severity: 'error', summary: 'Import Failed', detail: 'There was an error during the import process.' });
            }
        });
    }

    reset() {
        this.activeStep = 0;
        this.analysis = null;
        this.analyzing = false;
    }
}
